/**
 * @file Submission UI — A1DetailsReadonlySection.
 * A1 on Dataset Overview — same UI as Form A1 (A1Section + summaries).
 */

import React, { useEffect, useMemo, useState } from "react";
import type { FormSubSection } from "../../interfaces/formInterfaces";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/A1/FormA1.css";
import "./LifecycleStageDetailsReadonly.css";
import A1Section from "../../pages/Dashboard/Contributor/Stages/A1/components/A1Section";
import A1EnergySummary from "../../pages/Dashboard/Contributor/Stages/A1/components/A1EnergySummary";
import A1CarbonSummary from "../../pages/Dashboard/Contributor/Stages/A1/components/A1CarbonSummary";
import { useA1Prefill } from "../../pages/Dashboard/Contributor/Stages/A1/hooks/useA1Prefill";
import {
  perFinishedCarbonUnitLabel,
  perFinishedEnergyUnitLabel,
  readBackgroundFinishedDeclaredQty,
  resolveBackgroundFinishedMaterialUnitSymbol,
} from "../../pages/Dashboard/Contributor/Stages/A1-A3/a1a3SummaryFromSubmission";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import DatasetOverviewReadonlyBlock from "./datasetOverview/DatasetOverviewReadonlyBlock";
import {
  sumA1CarbonReferenceForSummary,
  sumA1EnergyMjForSummary,
} from "./lifecycleReadonlySummary";
import { normalizeRawMaterialsFromSubmission } from "./lifecycleReadonlyRawMaterials";
import type { AnyObj, ReferenceLabels } from "./readonlyFormDisplay";

const noop = () => {};

/** Type definition — A1Details Readonly Section Props. */
export type A1DetailsReadonlySectionProps = {
  submission: AnyObj;
};

const A1DetailsReadonlySection: React.FC<A1DetailsReadonlySectionProps> = ({
  submission,
}) => {
  const formSchema: AnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];

  const stage = formSchema.find((s) => s.code === "RAW_MATERIAL_EXTRACTION_A1");
  const sections: FormSubSection[] =
    stage && Array.isArray(stage.formSubSections)
      ? (stage.formSubSections as FormSubSection[])
      : [];

  const rawMaterials = useMemo(
    () => normalizeRawMaterialsFromSubmission(submission),
    [submission]
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [sectionRows, setSectionRows] = useState<Record<string, number>>({});
  const { prefillFromSubmission } = useA1Prefill();

  useEffect(() => {
    if (sections.length === 0) return;
    const initialRows: Record<string, number> = {};
    sections.forEach((s) => {
      if (s.code) initialRows[s.code] = Math.max(rawMaterials.length, 1);
    });
    setSectionRows(initialRows);
    setValues({});
    prefillFromSubmission(
      sections,
      submission,
      rawMaterials,
      setValues,
      setSectionRows,
      null
    );
  }, [submission, sections, rawMaterials, prefillFromSubmission]);

  const energyMj = useMemo(
    () => sumA1EnergyMjForSummary(submission),
    [submission]
  );

  const carbonKg = useMemo(
    () => sumA1CarbonReferenceForSummary(submission),
    [submission]
  );

  const finishedQty = useMemo(
    () => readBackgroundFinishedDeclaredQty(submission),
    [submission]
  );

  const finishedMaterialUnitSymbol = useMemo(
    () => resolveBackgroundFinishedMaterialUnitSymbol(submission),
    [submission]
  );

  const perFinishedEnergyUnit = useMemo(
    () => perFinishedEnergyUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnit = useMemo(
    () => perFinishedCarbonUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const energyPerFinished = useMemo(() => {
    if (finishedQty == null || energyMj <= 0) return null;
    return Math.round((energyMj / finishedQty) * 1000) / 1000;
  }, [energyMj, finishedQty]);

  const carbonPerFinished = useMemo(() => {
    if (finishedQty == null || carbonKg <= 0) return null;
    return Math.round((carbonKg / finishedQty) * 1000) / 1000;
  }, [carbonKg, finishedQty]);

  if (sections.length === 0) {
    return (
      <DatasetOverviewFormShell
        variant="lifecycle-a1"
        bare
        emptyMessage="No A1 form sections configured."
      />
    );
  }

  return (
    <DatasetOverviewFormShell variant="lifecycle-a1" bare>
      {sections.map((section) => {
        const code = section.code || section.name || String(section.id);
        const rowCount = sectionRows[code] || Math.max(rawMaterials.length, 1);

        return (
          <DatasetOverviewReadonlyBlock key={code} title={section.name ?? code}>
            <A1Section
              section={section}
              rowCount={rowCount}
              rawMaterials={rawMaterials}
              extractionProcesses={[]}
              values={values}
              onChange={noop}
              readonly
              referenceLabels={
                (submission.referenceLabels as ReferenceLabels) ?? undefined
              }
            />
            {code === "A1_EMBODIED_ENERGY" && (
              <A1EnergySummary
                energyMj={energyMj}
                energyPerDeclaredFinishedMj={energyPerFinished}
                perFinishedUnitLabel={perFinishedEnergyUnit}
              />
            )}
            {code === "A1_EMBODIED_CARBON" && (
              <A1CarbonSummary
                totalKgCo2e={carbonKg}
                perFinishedKgCo2e={carbonPerFinished}
                perFinishedUnitLabel={perFinishedCarbonUnit}
              />
            )}
          </DatasetOverviewReadonlyBlock>
        );
      })}
    </DatasetOverviewFormShell>
  );
};

/** React component — A1Details Readonly Section. */
export default A1DetailsReadonlySection;
